/** Weekly recurrence options for an assigned post-it. */
import type {
  WeeklyOptions as WeeklyOptionsType,
  MonthNumber,
  PostItColor,
} from '@/types/post-it.types';
import { cn } from '@/lib/utils';
import {
  TEXT_COLOR_MAP,
  BORDER_COLOR_MAP,
  POST_IT_MONTH_OPTIONS,
  POST_IT_WEEK_OF_MONTH_OPTIONS,
} from '@/components/post-its/post-it-configs';
import {
  getScheduleChipClasses,
  isMonthActive,
  toggleAllMonths,
  toggleTargetMonth,
} from '@/components/post-its/post-it-form-utils';

/** Props for {@link WeeklyOptions}. */
interface WeeklyOptionsProps {
  value: WeeklyOptionsType;
  onChange: (value: WeeklyOptionsType) => void;
  color?: PostItColor;
}

/** Weekly recurrence options for an assigned post-it. */
export function WeeklyOptions({ value, onChange, color = 'jaune' }: Readonly<WeeklyOptionsProps>) {
  const textColor = TEXT_COLOR_MAP[color] || TEXT_COLOR_MAP.jaune;
  const borderColor = BORDER_COLOR_MAP[color] || BORDER_COLOR_MAP.jaune;

  const handleMonthToggle = (month: MonthNumber) => {
    onChange({ ...value, targetMonths: toggleTargetMonth(value.targetMonths, month) });
  };

  return (
    <div className="space-y-3">
      <div className="space-y-1">
        <p className={cn('text-xs font-medium', textColor)}>Semaine du mois</p>
        <div className="flex flex-wrap gap-1">
          {POST_IT_WEEK_OF_MONTH_OPTIONS.map((w) => (
            <button
              key={String(w.value)}
              type="button"
              onClick={() => onChange({ ...value, weekOfMonth: w.value })}
              className={cn(
                'px-2 py-1 rounded text-xs border transition-all',
                borderColor,
                getScheduleChipClasses(color, value.weekOfMonth === w.value),
              )}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <p className={cn('text-xs font-medium', textColor)}>Mois ciblés</p>
          <button
            type="button"
            onClick={() =>
              onChange({ ...value, targetMonths: toggleAllMonths(value.targetMonths) })
            }
            className={cn(
              'text-xs px-2 py-0.5 rounded border transition-all',
              borderColor,
              getScheduleChipClasses(color, value.targetMonths === 'all'),
            )}
          >
            Tous
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {POST_IT_MONTH_OPTIONS.map((m) => {
            const active = isMonthActive(value.targetMonths, m.value as MonthNumber);
            return (
              <button
                key={m.value}
                type="button"
                onClick={() => handleMonthToggle(m.value as MonthNumber)}
                className={cn(
                  'w-9 py-1 rounded text-xs border transition-all',
                  borderColor,
                  getScheduleChipClasses(color, active),
                )}
              >
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
